import { Command } from 'commander';
import { FileSystemAdapter } from '../../src/utils/file-system-adapter.js';
import { IssueService } from '../../src/services/issue-service.js';
import { PathnameService } from '../../src/services/pathname-service.js';
import { loadLlmailConfig } from '../../src/utils/config.js';
import { logger } from '../../src/utils/logger.js';
import { FrontmatterMetadata } from '../../src/types.js';
import { IntentService } from '../../src/services/intent-service.js';
import { FileService } from '../../src/services/file-service.js';
import { FileFormatValidator } from '../../src/validation/file-format-validator.js';
import { MetadataFormatValidator } from '../../src/validation/metadata-format-validator.js';
import { FileSystemValidator } from '../../src/validation/file-system-validator.js';
import { join as pathJoin } from 'path';

export interface DoneOptions {
  id?: string;
  state?: string;
  sourcePath?: string;
  frontmatter?: Record<string, any>;
}

export interface DoneResult {
  success: boolean;
  error?: string;
  path?: string;
  warnings?: string[];
}

/**
 * Mark an issue as done
 */
export async function done(fs: FileSystemAdapter, options: DoneOptions): Promise<DoneResult> {
  try {
    // Load config and initialize services
    const config = await loadLlmailConfig(fs);
    const intentService = new IntentService(config);
    const pathnameService = new PathnameService(config, intentService);
    const fileService = new FileService(fs, pathnameService);
    const fileFormatValidator = new FileFormatValidator();
    const metadataValidator = new MetadataFormatValidator();
    const fileSystemValidator = new FileSystemValidator(fs);
    const issueService = new IssueService(
      pathnameService,
      fs,
      intentService,
      fileService,
      fileFormatValidator,
      metadataValidator,
      fileSystemValidator
    );

    // Find the file
    let filePath: string;
    if (options.id) {
      const foundPath = await issueService.fileMethods.findFileById(options.id);
      if (!foundPath) {
        throw new Error(`No issue found with id ${options.id}`);
      }
      filePath = foundPath;
    } else if (options.sourcePath) {
      filePath = options.sourcePath;
    } else {
      return {
        success: false,
        error: 'Must specify either ID or source path'
      };
    }

    // Get current metadata
    const metadata = await issueService.frontmatterMethods.getFrontmatterMetadata(filePath);
    if (!metadata) {
      throw new Error(`No metadata found in file ${filePath}`);
    }

    // Update metadata
    const newMetadata: FrontmatterMetadata = {
      ...metadata,
      active: false,
      state: options.state ?? undefined,
      inactive_timestamp: new Date().toISOString()
    };

    if (options.frontmatter) {
      // Merge custom frontmatter on top of the existing metadata
      // but re-assert the vital fields that must not be overwritten inadvertently
      Object.assign(newMetadata, options.frontmatter);
      newMetadata.id = metadata.id;
      newMetadata.type = metadata.type;
      newMetadata.active = false;
      newMetadata.inactive_timestamp = new Date().toISOString();
    }

    // Validate metadata
    const validation = issueService.frontmatterMethods.validateMetadata(newMetadata);
    if (!validation.valid) {
      throw new Error(`Invalid metadata: ${validation.errors.join(', ')}`);
    }

    // Check if target exists
    const location = pathnameService.deriveFileLocation(newMetadata);
    const targetPath = pathJoin(location.dirname, location.basename);
    if (targetPath !== filePath && await fs.exists(targetPath)) {
      return {
        success: false,
        error: 'Target file already exists'
      };
    }

    // Move file and update metadata
    const newPath = await issueService.fileMethods.saveAndMoveFile(filePath, newMetadata);

    return {
      success: true,
      path: newPath,
      warnings: validation.warnings
    };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : String(err)
    };
  }
} 